/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useRef } from 'react';
import {
  Chip,
  CircularProgress,
  TextField as MTextField,
  AutocompleteRenderInputParams,
} from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { Close } from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import { useSelector } from 'react-redux';

import { activeUserHasPermission } from 'services/user/redux';
import { stopInputPropagation } from 'helpers';

import { AutoCompleteCmp } from './types';
import { useAutocomplete } from './styled';

const AutoComplete: AutoCompleteCmp<any> = (props: any) => {
  const {
    label,
    name,
    required,
    placeholder,
    error,
    helperText,
    getOptionLabel,
    additionalInputProps,
    className,
    disabled = false,
    autoSelect = true,
    classes,
    dataQa,
    statusCustom,
    permissions = [],
    isLoading,
    inputRef,
    doNotOverwriteGetOptionLabel = false,
    ...otherProps
  } = props;

  const classesInner = useAutocomplete(props);

  const inputElement = inputRef || useRef<HTMLInputElement>(null);

  const canEdit = useSelector(activeUserHasPermission(permissions));

  const isDisabled = disabled || !canEdit;

  const resolveOptionLabel = useCallback((option: any) => {
    if (!getOptionLabel || !option) {
      return option || '';
    }

    return getOptionLabel(option);
  }, []);

  const handleOnOpen = useCallback(() => {
    if (inputElement.current) {
      inputElement.current.focus();
    }
  }, [inputElement]);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const endAdornment = params.InputProps.endAdornment;

      return (
        <MTextField
          className={'redesign'}
          {...params}
          label={label}
          name={name}
          fullWidth
          InputLabelProps={{ shrink: true, margin: 'dense' }}
          required={required}
          placeholder={placeholder}
          onClick={stopInputPropagation}
          error={error}
          helperText={helperText}
          variant="outlined"
          size="small"
          disabled={disabled}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
            'data-qa': dataQa,
            'data-lpignore': 'true',
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {endAdornment}
              </React.Fragment>
            ),
            ...additionalInputProps,
            inputRef: inputElement,
          }}
        >
          {statusCustom && label === 'Drop Ship' && (
            <Chip
              label={statusCustom}
              size="small"
              classes={{
                root: classesInner.status,
                label: classesInner.statusLabel,
              }}
              color="secondary"
            />
          )}
        </MTextField>
      );
    },
    [
      error,
      inputElement,
      helperText,
      disabled,
      additionalInputProps,
      statusCustom,
    ]
  );

  return (
    <Autocomplete
      {...otherProps}
      // TODO : RESTYLING : minor style adjustment needed for now until we use the FBOAutoSelect component
      style={{ marginTop: '0px', marginBottom: '0px' }}
      className={`${'redesign'} ${classesInner.minWidth} ${className}`}
      classes={{
        popper: classesInner.popup,
        option: classesInner.option,
        ...classes,
      }}
      getOptionLabel={
        doNotOverwriteGetOptionLabel ? getOptionLabel : resolveOptionLabel
      }
      renderInput={renderInput}
      disabled={isDisabled}
      onOpen={handleOnOpen}
      autoSelect={autoSelect}
      clearIcon={
        dataQa && (
          <Close
            fontSize="small"
            data-qa={`${dataQa}-autocomplete-clear-icon`}
          />
        )
      }
      popupIcon={
        dataQa && (
          <ArrowDropDown
            fontSize="small"
            data-qa={`${dataQa}-autocomplete-dropdown-icon`}
          />
        )
      }
    />
  );
};

export default React.memo(AutoComplete);
